import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { reapplyAllFixes } from '../../utils/navigationFixes';

/**
 * RouterListener Component
 *
 * This component listens for React Router navigation events and reapplies
 * fixes for common issues like ResizeObserver errors and SVG attribute errors.
 *
 * It follows Module M7 (Integration Points) principles by providing a clear
 * integration point between React Router and our error handling system.
 *
 * It also follows Module M15 (Error Handling) principles by implementing
 * defensive coding practices to prevent errors during navigation.
 */
interface RouterListenerProps {
  children?: React.ReactNode;
}

/**
 * RouterListener Component
 *
 * This component listens for location changes and reapplies fixes
 * after each navigation event. It uses both the navigationFixes utility
 * and the global __CONNECTPATH_UTILS functions from preload.js.
 */
const RouterListener: React.FC<RouterListenerProps> = ({ children }) => {
  const location = useLocation();
  const prevLocationRef = useRef<string>(location.pathname);
  const fixTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Effect to run when the location changes
  useEffect(() => {
    // Only run if the pathname has actually changed
    if (prevLocationRef.current !== location.pathname) {
      console.log(`Navigation detected: ${prevLocationRef.current} → ${location.pathname}`);
      prevLocationRef.current = location.pathname;
      
      // Clear any existing timeout
      if (fixTimeoutRef.current) {
        clearTimeout(fixTimeoutRef.current);
      }
      
      // Apply fixes immediately
      try {
        // Call the utility function from navigationFixes
        reapplyAllFixes();
        
        // Call the global function from preload.js if available
        if (window.__CONNECTPATH_UTILS?.onRouteChange) {
          window.__CONNECTPATH_UTILS.onRouteChange();
        }
      } catch (error) {
        console.warn('Error applying navigation fixes:', error);
      }
      
      // Apply fixes again after a short delay to catch any late-rendered components
      fixTimeoutRef.current = setTimeout(() => {
        try {
          reapplyAllFixes();
          
          if (window.__CONNECTPATH_UTILS?.onRouteChange) {
            window.__CONNECTPATH_UTILS.onRouteChange();
          }
          
          // Fix all SVG elements
          if (window.__CONNECTPATH_UTILS?.fixAllSvgElements) {
            window.__CONNECTPATH_UTILS.fixAllSvgElements();
          }
          
          console.log('Navigation fixes reapplied after delay');
        } catch (error) {
          console.warn('Error applying delayed navigation fixes:', error);
        }
        
        fixTimeoutRef.current = null;
      }, 300);
    }
    
    // Return cleanup function
    return () => {
      if (fixTimeoutRef.current) {
        clearTimeout(fixTimeoutRef.current);
        fixTimeoutRef.current = null;
      }
    };
  }, [location]);
  
  // This component doesn't render anything itself
  return <>{children}</>;
};

export default RouterListener;